// "<year>: <title>" section headings (e.g. "2027: Congress Wakes Up") double as
// the jump targets for the HUD year dropdown — parsed from the DOM here so the
// dropdown stays in sync with the rendered section headings.

import { PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR } from '@/app/playbook/branch-node/planPrerenderWarmupScan';

export type YearHeading = { id: string; year: number; after: string };

const YEAR_HEADING_RE = /(?:^|\s)(\d{4}):\s*(.*)/;

// Parses one heading element, or null if it isn't a "<year>: <title>" heading.
const parseYearHeading = (el: HTMLElement): YearHeading | null => {
  const text = el.textContent?.trim() ?? '';
  const match = text.match(YEAR_HEADING_RE);
  if (!match || !el.id) return null;
  return { id: el.id, year: Number(match[1]), after: text.slice(text.indexOf(':') + 1).trim() };
};

const isYearHeading = (heading: YearHeading | null): heading is YearHeading => heading !== null;

// Defensive against repeat ids (DOM ids are normally unique); keeps the first.
const dedupeById = (headings: YearHeading[]): YearHeading[] => {
  const seen = new Set<string>();
  return headings.filter(h => (seen.has(h.id) ? false : (seen.add(h.id), true)));
};

// Every main-story year-section heading in the document, in reading order.
// H1 only: the year sections of the selected reading path ("2030: Plan A is
// Established") are H1s, while the H2 year headings — the POV sections'
// per-year beats, alternate-timeline boxes — are sub-stories that don't
// belong in a year table of contents.
export const getYearHeadings = (): YearHeading[] => {
  const elements = [...document.querySelectorAll<HTMLElement>(
    `h1${PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR}`
  )];
  return dedupeById(elements.map(parseYearHeading).filter(isYearHeading));
};
